import { Box, Flex } from 'components/elements'
import { FileIcon, EyeIcon, DownloadIcon } from 'components/icons'
import { FileExtension } from 'features/incidents/components/enums'
import { useEffect, useRef } from 'react'
import { styled } from '@stitches/react'
import { downloadPDF, getFileInfoFromUrl, openFullscreen, openInNewTab } from 'utils/function'
import { TextOverFlowCSS } from 'features/incidents/components/style'
import { TERMINOLOGY } from 'utils/enum'

type ImageDocBoxType = {
  scourceData: string[] | undefined
  documentName?: string
  hideLabel?: boolean
}
const ImageDocBox = ({ scourceData, documentName, hideLabel }: ImageDocBoxType) => {
  const imageRefs: React.MutableRefObject<(HTMLImageElement | null)[]> = useRef([])

  useEffect(() => {
    imageRefs.current = imageRefs.current.slice(0, scourceData?.length) //imageRefs array  as many elements as the documentUrl array.
  }, [scourceData])

  return (
    <File css={FileBox}>
      {scourceData &&
        scourceData?.map((url: string, idx: number) => (
          <Box key={idx}>
            {!hideLabel && (
              <Box as="h4" css={{ marginBottom: '1.25rem' }}>
                {`${TERMINOLOGY.warranty.singular} Document ${idx + 1}`}
              </Box>
            )}
            <Box css={imgBoxOverlay}>
              {getFileInfoFromUrl(url)?.type === FileExtension.PDF ? (
                <FileIcon />
              ) : getFileInfoFromUrl(url)?.type === FileExtension.DOCX ? (
                <FileIcon />
              ) : (
                <Box as="img" src={url} alt={documentName?.[idx]} css={imgBox} />
              )}
              <Box className="overflow-box" css={OverFlowBoxCSS}>
                <Flex css={{ width: '100%', gap: '.5rem' }}>
                  <Flex css={{ flexShrink: 0 }}>
                    <FileIcon />
                  </Flex>
                  <Box as="p" css={{ width: 'calc(100% - 38px)', wordBreak: 'break-word' }}>
                    {documentName?.[idx]}
                  </Box>
                </Flex>
                <Flex css={IconsButtonCss}>
                  {getFileInfoFromUrl(url)?.type === FileExtension.PDF ? (
                    <>
                      <Box css={IconsButtonBoxCss} onClick={() => downloadPDF(url, documentName?.[idx].replace('.pdf', ''))}>
                        <DownloadIcon />
                      </Box>
                      <Box css={IconsButtonBoxCss} onClick={() => openInNewTab(url)}>
                        <EyeIcon />
                      </Box>
                    </>
                  ) : (
                    <>
                      {getFileInfoFromUrl(url)?.type === FileExtension.DOCX ? (
                        <Box css={IconsButtonBoxCss} onClick={() => openInNewTab(url)}>
                          <DownloadIcon />
                        </Box>
                      ) : (
                        <>
                          <Box as="a" href={url} download css={IconsButtonBoxCss}>
                            <DownloadIcon />
                          </Box>
                          <Box as="a" css={IconsButtonBoxCss} onClick={() => openFullscreen({ idx, elementRefs: imageRefs })}>
                            <Box as="img" css={{ width: 0, height: 0 }} src={url} alt={documentName?.[idx]} id={`image${idx}`} ref={el => (imageRefs.current[idx] = el)} />
                            <EyeIcon />
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Flex>
              </Box>
              <Box css={textOverFlowBox}>
                <Flex css={{ flexShrink: 0 }}>
                  <FileIcon />
                </Flex>
                <Box as="p" css={TextOverFlowCSS} style={{ width: 'calc(100% - 38px)', cursor: 'pointer', whiteSpace: 'pre' }}>
                  {documentName?.[idx]}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
    </File>
  )
}

export default ImageDocBox

const File = styled('div', {
  'display': 'flex',
  'flexDirection': 'column',

  '& > .icons': {
    'columnGap': 15,
    'alignItems': 'center',
    'borderRadius': '$sm',
    'padding': '.75rem 1.5rem',
    'width': 'calc(50% - 78px)',
    'border': '1px solid $border',
    'display': 'flex',

    '.title': {
      position: 'relative',
      top: '-5px',
      fontSize: '$base',
      fontWeight: '$bold',
      lineHeight: '22px',
      textAlign: 'left',
      marginLeft: '10px',
      cursor: 'pointer',
    },
  },
})

export const FileBox = {
  columnGap: 15,
  marginBottom: '1rem',
  flexDirection: 'row',
  flexWrap: 'wrap',
}

export const imgBoxOverlay = {
  'display': 'flex',
  'justifyContent': 'center',
  'width': 200,
  'height': 200,
  'position': 'relative',
  'marginBottom': 8,
  'borderRadius': '.75rem',
  'overflow': 'hidden',

  '& > svg': {
    width: '80%',
    height: '80%',
  },

  '&:hover': {
    '.overflow-box': {
      pointerEvents: 'all',
      opacity: 1,
    },
  },

  '&:after': {
    content: '',
    display: 'block',
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    background: '$aboutGradient',
  },
}

export const textOverFlowBox = {
  position: 'absolute',
  bottom: 0,
  background: '$black80',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  columnGap: '.5rem',
  padding: '.5rem',
  width: '100%',
}

export const imgBox = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}

export const IconsButtonCss = {
  display: 'flex',
  gap: '.5rem',
}

export const OverFlowBoxCSS = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  position: 'absolute',
  bottom: 0,
  columnGap: '.5rem',
  padding: '.75rem',
  background: '$blue6',
  pointerEvents: 'none',
  opacity: 0,
  zIndex: 2,
}

export const IconsButtonBoxCss = {
  display: 'flex',
  justifyContent: 'center',
  padding: '.75rem',
  background: '$background',
  borderRadius: '.5rem',
  cursor: 'pointer',

  svg: {
    width: 16,
    height: 16,
  },
}
